// src/data/disciplineOfficerNotes.js
//
// RULING 305.9 (by delegation, 2026-10-11, Record 637): ruling 305.7 applied to a DISCIPLINE case. Bob, 305.7: "A -
// agreed, but that must be made absolutely clear to the representatives - silly stupid comments can cause a lot of
// unnecessary damage - its on them to avoid it". Record 634.4 left this half unbuilt: the case Notes tab still told
// every officer "All parties see these notes".
//
// An officer's note on a case lives in disciplineCases/{caseId}/officerNotes, not in the case's own `notes` (which the
// named member reads, and which keep the system entries and every note written before today - nothing is moved). It is
// private to the union - the Local Chairperson of the territory, the General Chairperson of the committee, and the
// administrator - while the union works the case; the case going forward to the General Chairperson is the union's own
// business (305.6) and releases nothing. It reaches the member it names at the EARLIEST of:
//   - a decision being recorded on the case   (decidedAt first set)         -> 'decided'
//   - the case being resolved                  (status becomes 'resolved')  -> 'resolved'
//   - SURFACE_DAYS after it was written        (ruling 148's cap, kept)     -> 'thirty_days'
//   - the officer sharing it as he writes it                                -> 'shared'
// No configuration produces a never-surfaced note (147): only the server sets visibleToMember (functions
// surfaceDisciplineOfficerNotesOnRelease and surfaceDueOfficerNotes); firestore.rules lets no client edit or delete one.
//
// PURE. No Firestore, no clock. Shared with the server (scripts\sync-shared-modules.js). The record a note is written as
// is officerNotes.js newOfficerNote() - one shape for both, so one rule shape checks both.
import { toMs, surfaceDueMs, SURFACE_DAYS } from './officerNotes';

export const DISCIPLINE_SURFACE_WHY = Object.freeze({
  shared: 'shared by the officer when he wrote it',
  decided: 'a decision was recorded on the case',
  resolved: 'the case was resolved',
  thirty_days: SURFACE_DAYS + ' days after it was written',
});

// What every officer reads above the box before he writes.
export const DISCIPLINE_OFFICER_NOTE_WARNING =
  'The member this case names WILL read this note. It stays between the union\'s officers only until a decision is '
  + 'recorded on the case or the case is resolved, or ' + SURFACE_DAYS + ' days after you write it - whichever comes '
  + 'first. Nothing written here is ever edited or deleted. Write nothing you would not say to the member yourself.';

// The case left the union's hands between two versions of it. Returns the reason key, or null. A decision recorded
// again after a reopening (decidedAt moves) releases again, as a second return of a claim does.
export function disciplineReleaseReason(before, after) {
  const b = before || {}, a = after || {};
  const da = toMs(a.decidedAt), db = toMs(b.decidedAt);
  if (da != null && da !== db) return 'decided';
  if (a.status === 'resolved' && b.status !== 'resolved') return 'resolved';
  return null;
}

// The line an officer sees under a note: who can read it, and from when.
export function disciplineVisibilityLine(note) {
  if (note && note.visibleToMember) {
    const at = toMs(note.surfacedAt);
    return 'The member can read this' + (at ? ' since ' + new Date(at).toISOString().slice(0, 10) : '') + ' - '
      + (DISCIPLINE_SURFACE_WHY[note.surfacedWhy] || 'released') + '.';
  }
  const due = surfaceDueMs(note);
  return 'Officers only for now. The member reads it when a decision is recorded or the case is resolved'
    + (due ? ', and no later than ' + new Date(due).toISOString().slice(0, 10) : '') + '.';
}

// Who writes officer notes on a case: an officer or the administrator, on a case that is out of draft.
export function writesDisciplineOfficerNotes(user, disciplineCase) {
  if (!user || !disciplineCase || disciplineCase.status === 'draft') return false;
  return user.isAdmin === true || ['admin', 'general_chair', 'local_chair'].includes(String(user.role || ''));
}
